import { green } from '@ant-design/colors';
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
  message
} from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import CustomDatePicker from '../../components/CustomDatePicker';
import { ESTADOS, NACIONALIDADES } from '../../constants';
import { convertDateStringToFormat, errorHandler } from '../../utils';
import { instanceRef } from '../../utils/axios';

const { Option } = Select;

const getOptionsForNacionalidad = () => {
  return NACIONALIDADES.map(({ descripcion }) => (
    <Option key={descripcion} value={descripcion}>
      {descripcion}
    </Option>
  ));
};

const getOptionsForEstadoCivil = () => {
  return ESTADOS.map(({ descripcion }) => (
    <Option key={descripcion} value={descripcion}>
      {descripcion}
    </Option>
  ));
};

const getOptionsForSexo = () => {
  const sexos = ['Femenino', 'Masculino'];
  return sexos.map((value) => (
    <Option key={value} value={value}>
      {value}
    </Option>
  ));
};

const EditableCell = ({
  editing,
  dataIndex,
  record,
  children,
  editableCell
}) => {
  let inputNode = null;
  let optionsSelect = [];
  if (dataIndex && record[dataIndex]?.name) {
    const name = dataIndex && record[dataIndex]?.name;
    if (name === 'nacionalidad') {
      optionsSelect = getOptionsForNacionalidad();
    } else if (name === 'estadoCivil') {
      optionsSelect = getOptionsForEstadoCivil();
    } else if (name === 'sexo') {
      optionsSelect = getOptionsForSexo();
    }
  }

  switch (dataIndex && record[dataIndex]?.type) {
    case 'input':
      inputNode = <Input />;
      break;
    case 'select':
      inputNode = <Select>{optionsSelect}</Select>;
      break;
    case 'date':
      inputNode = (
        <CustomDatePicker
          style={{ width: '100%' }}
          placeholder="DD/MM/YYYY"
          format="DD/MM/YYYY"
        />
      );
      break;
    default:
      inputNode = <Input />;
  }

  const nameCol = dataIndex ? record[dataIndex]?.name : null;
  const content =
    dataIndex === 'emision' ? <strong>{children}</strong> : children;

  return (
    <td>
      {editing && nameCol && editableCell ? (
        <Form.Item
          name={[dataIndex, nameCol]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: 'Requerido' }]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        content
      )}
    </td>
  );
};

const AutorizacionDatos = ({
  autorizacion,
  idAseguradora,
  datosConfirmados,
  setDatosConfirmados,
  setHabilitarBotonAprobar,
  form
}) => {
  const [loading, setLoading] = useState(true);
  const [personaDatos, setPersonaDatos] = useState([]);
  const [editableEmisionPersona, setEditableEmisionPersona] = useState(true);
  // const [datosConfirmados, setDatosConfirmados] = useState(false);

  const renderColumnPersona = (record, index) => {
    let { value } = record;
    if (record.requiereRevision) {
      return (
        <Tooltip title="Este dato  requiere autorización">
          <Typography.Text type="warning">
            <span>
              {record.value}
              <InfoCircleOutlined
                style={{
                  color: 'rgba(0,0,0,.45)',
                  marginRight: '5px'
                }}
              />
            </span>
          </Typography.Text>
        </Tooltip>
      );
    }

    if (
      record.actualizado ||
      (!editableEmisionPersona && index === 'emision')
    ) {
      const datoFormateado =
        record.type === 'date' ? moment(value).format('DD/MM/YYYY') : value;
      value = <strong>{datoFormateado}</strong>;
    }
    return value;
  };

  const components = {
    body: {
      cell: EditableCell
    }
  };
  const columnsPersona = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label'
    },
    {
      title: 'Cotización (Ejecutivo)',
      dataIndex: 'cotizacion',
      key: 'cotizacion',
      render: renderColumnPersona
    },
    {
      title: 'Cédula de Identidad',
      dataIndex: 'imagen',
      key: 'imagen',
      render: renderColumnPersona
    },
    {
      title: 'Control Sistema',
      dataIndex: 'controlSegu',
      key: 'controlSegu',
      render: renderColumnPersona
    },

    {
      title: 'Solicitud (Usuario)',
      dataIndex: 'solicitud',
      key: 'solicitud'
    },
    {
      title: (
        <>
          <Space>
            Sistema Core
            {autorizacion.estado === 'PENDIENTE' && editableEmisionPersona && (
              <Tooltip title="Recargar datos del Sistema Core">
                <Button
                  key="reloadButton"
                  icon={<ReloadOutlined />}
                  size="small"
                  onClick={() => getDatosSistemaCore()}
                />
              </Tooltip>
            )}
          </Space>
        </>
      ),
      dataIndex: 'sistemaExterno',
      key: 'sistemaExterno'
    },
    {
      title: (
        <>
          <Space>
            Datos para emisión
            <Tooltip title="Datos confirmados">
              <Typography.Text hidden={!datosConfirmados}>
                <CheckCircleOutlined
                  style={{
                    color: green.primary
                  }}
                />
              </Typography.Text>
            </Tooltip>
          </Space>
        </>
      ),
      dataIndex: 'emision',
      key: 'emision',
      width: 300,
      render: (record) => renderColumnPersona(record, 'emision'),
      editable: editableEmisionPersona
    }
  ];

  const columns = columnsPersona.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === 'emision' ? record.emision.type : '',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: true,
        editableCell: editableEmisionPersona
      })
    };
  });

  const getDatosSistemaCore = async () => {
    setLoading(true);
    try {
      const cedulaObject = personaDatos.find((item) => {
        if (item.key === 'cedula') {
          return item;
        }
        return null;
      });
      if (cedulaObject) {
        const personaId = autorizacion.detalle?.persona?.sistemaCore?.id;
        // Se obtiene datos actualizados y comparativos.Se actualizan los datos en datos_externos de la persona.
        const response = await instanceRef.instance.post(
          `autorizacion/datos/sistemacore?idAseguradora=${idAseguradora}`,
          {
            tipo: 1, // tipo = Cedula en Sebaot,
            nro_documento: cedulaObject.emision.value,
            personaId
          }
        );
        if (response.data?.data) {
          const datosComparativosSistemaCore = response.data?.data;
          setPersonaDatos({
            ...personaDatos,
            sistemaCore: datosComparativosSistemaCore
          });

          const autorizacionNew = {
            ...autorizacion,
            detalle: {
              ...autorizacion.detalle,
              persona: {
                ...autorizacion.detalle?.persona,
                sistemaCore: datosComparativosSistemaCore
              }
            }
          };
          await instanceRef.instance.put(
            `/autorizacion/detalle/${autorizacionNew.id}`,
            {
              autorizacion: { ...autorizacionNew }
            }
          );
        }

        setLoading(false);
      } else {
        message.warning(
          'El dato de Cédula de Identidad para emitir es obligatorio'
        );
        setLoading(false);
      }
    } catch (error) {
      errorHandler(error, error.message);
    }
  };
  const cargarDatosPersona = useCallback(() => {
    const autorizacionPersona = autorizacion.detalle?.persona;
    const personaDatosList = [
      {
        key: 'cedula',
        label: 'Cédula de Identidad',
        cotizacion: {
          requiereRevision: false,
          value: ''
        },
        imagen: {
          requiereRevision: autorizacionPersona.imagen?.cedulaRequiereRevision,
          value: autorizacionPersona.imagen?.cedula
        },
        controlSegu: {
          requiereRevision: autorizacionPersona.bcp?.cedulaRequiereRevision,
          value: autorizacionPersona.bcp?.cedula
        },
        sistemaCore: {
          requiereRevision:
            autorizacionPersona.sistemaCore?.cedulaRequiereRevision,
          value: autorizacionPersona.sistemaCore?.cedula
        },
        solicitud: autorizacionPersona.solicitud?.cedula,
        emision: {
          type: 'input',
          name: 'cedula',
          value: autorizacionPersona.emision?.cedula,
          actualizado: autorizacionPersona.emision?.actualizado || false
        }
      },
      {
        key: 'nombres',
        label: 'Nombre y Apellido',
        cotizacion: {
          // autorizacionPersona.cotizacion?.nombresRequiereRevision,
          requiereRevision: false, // Por requerimiento de Tito este dato no debe caer
          value: autorizacionPersona.cotizacion?.nombres
        },
        imagen: {
          requiereRevision: autorizacionPersona.imagen?.nombresRequiereRevision,
          value: autorizacionPersona.imagen?.nombres
        },
        controlSegu: {
          requiereRevision: autorizacionPersona.bcp?.nombresRequiereRevision,
          value: autorizacionPersona.bcp?.nombres
        },
        sistemaCore: {
          requiereRevision:
            autorizacionPersona.sistemaCore?.nombresRequiereRevision,
          value: autorizacionPersona.sistemaCore?.nombres
        },
        solicitud: autorizacionPersona.solicitud?.nombres,
        emision: {
          type: 'input',
          name: 'nombres',
          value: autorizacionPersona.emision?.nombres,
          actualizado: autorizacionPersona.emision?.actualizado || false
        }
      },
      {
        key: 'fechaNacimiento',
        label: 'Fecha de Nacimiento',
        cotizacion: '',
        imagen: {
          requiereRevision:
            autorizacionPersona.imagen?.fechaNacimientoRequiereRevision,
          value: autorizacionPersona.imagen?.fechaNacimiento
            ? convertDateStringToFormat(
                autorizacionPersona.imagen?.fechaNacimiento
              )
            : ''
        },
        controlSegu: {
          requiereRevision:
            autorizacionPersona.bcp?.fechaNacimientoRequiereRevision,
          value: autorizacionPersona.bcp?.fechaNacimiento
            ? convertDateStringToFormat(
                autorizacionPersona.bcp?.fechaNacimiento.toString()
              )
            : ''
        },
        sistemaCore: {
          requiereRevision:
            autorizacionPersona.sistemaCore?.fechaNacimientoRequiereRevision,
          value: autorizacionPersona.sistemaCore?.fechaNacimiento
            ? convertDateStringToFormat(
                autorizacionPersona.sistemaCore?.fechaNacimiento.toString()
              )
            : ''
        },
        solicitud: autorizacionPersona.solicitud?.fechaNacimiento
          ? convertDateStringToFormat(
              autorizacionPersona.solicitud?.fechaNacimiento
            )
          : '',
        emision: {
          type: 'date',
          name: 'fechaNacimiento',
          value: autorizacionPersona.emision?.fechaNacimiento
            ? autorizacionPersona.emision?.fechaNacimiento
            : null,
          actualizado: autorizacionPersona.emision?.actualizado || false
        }
      },
      {
        key: 'sexo',
        label: 'Sexo',
        cotizacion: '',
        imagen: {
          requiereRevision: autorizacionPersona.imagen?.sexoRequiereRevision,
          value: autorizacionPersona.imagen?.sexo
        },
        controlSegu: '',
        sistemaCore: {
          requiereRevision:
            autorizacionPersona.sistemaCore?.sexoRequiereRevision,
          value: autorizacionPersona.sistemaCore?.sexo
        },
        solicitud: autorizacionPersona.solicitud?.sexo,
        emision: {
          type: 'select',
          name: 'sexo',
          value: autorizacionPersona.emision?.sexo,
          actualizado: autorizacionPersona.emision?.actualizado || false
        }
      },
      {
        key: 'estadoCivil',
        label: 'Estado Civil',
        cotizacion: '',
        imagen: {
          requiereRevision:
            autorizacionPersona.imagen?.estadoCivilRequiereRevision,
          value: autorizacionPersona.imagen?.estadoCivil
        },
        controlSegu: '',
        sistemaCore: {
          requiereRevision:
            autorizacionPersona.sistemaCore?.estadoCivilRequiereRevision,
          value: autorizacionPersona.sistemaCore?.estadoCivil
        },
        solicitud: autorizacionPersona.solicitud?.estadoCivil,
        emision: {
          type: 'select',
          name: 'estadoCivil',
          value: autorizacionPersona.emision?.estadoCivil,
          actualizado: autorizacionPersona.emision?.actualizado || false
        }
      },
      {
        key: 'nacionalidad',
        label: 'Nacionalidad',
        cotizacion: '',
        imagen: {
          requiereRevision:
            autorizacionPersona.imagen?.nacionalidadRequiereRevision,
          value: autorizacionPersona.imagen?.nacionalidad
        },
        controlSegu: '',
        sistemaCore: '',
        solicitud: autorizacionPersona.solicitud?.nacionalidad,
        emision: {
          type: 'select',
          name: 'nacionalidad',
          value: autorizacionPersona.emision?.nacionalidad,
          actualizado: autorizacionPersona.emision?.actualizado || false
        }
      }
    ];
    setPersonaDatos(personaDatosList);

    const initialValues = {};
    personaDatosList.forEach((data) => {
      if (data.emision && data.emision.name && data.emision.value) {
        if (data.emision.type !== 'date') {
          initialValues[data.emision.name] = data.emision.value;
        } else if (moment(data.emision.value).isValid) {
          initialValues[data.emision.name] = moment(data.emision.value);
        }
      }
    });
    form.setFieldsValue({
      emision: { ...initialValues }
    });
    setLoading(false);
  }, [autorizacion.detalle?.persona, form]);

  useEffect(() => {
    if (datosConfirmados) {
      setEditableEmisionPersona(false);
    } else {
      setEditableEmisionPersona(true);
    }
  }, [datosConfirmados]);

  useEffect(() => {
    if (autorizacion && autorizacion.detalle?.persona) {
      cargarDatosPersona();
      if (autorizacion.estado !== 'PENDIENTE') {
        setEditableEmisionPersona(false);
      }
    }
  }, [
    autorizacion,
    cargarDatosPersona,
    setDatosConfirmados,
    setHabilitarBotonAprobar
  ]);

  return (
    <div style={{ overflowX: 'auto', textAlign: 'center' }}>
      <Spin
        spinning={loading}
        size="small"
        tip="Cargando..."
        style={{ overflowX: 'auto', textAlign: 'center' }}
      >
        {personaDatos && (
          <>
            <Form form={form} component={false}>
              <Table
                components={components}
                dataSource={personaDatos}
                columns={columns}
                pagination={false}
                size="small"
                style={{ overflowX: 'auto' }}
                rowKey={(record) => record.key}
                rowClassName={(record) =>
                  record.controlSegu?.requiereRevision ||
                  record.cotizacion?.requiereRevision ||
                  record.imagen?.requiereRevision ||
                  record.bcp?.requiereRevision ||
                  record.sistemaCore?.requiereRevision
                    ? 'ant-table-row-selected'
                    : ''
                }
              />
              <br />
            </Form>
          </>
        )}
      </Spin>
    </div>
  );
};

export default AutorizacionDatos;
